import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FaWhatsapp, FaCheckCircle, FaFileInvoiceDollar,
  FaReceipt, FaShieldAlt, FaBolt, FaHeadset,
  FaFileAlt, FaClock,
} from 'react-icons/fa';
import { FaFileCircleCheck } from 'react-icons/fa6';
import styles from './NotaFiscal.module.css';
import PageSEO from '../../components/PageSEO/PageSEO';

/* ─── constantes ──────────────────────────────────────────────── */
const WA_LINK =
  'https://wa.me/5548988393447?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20a%20API%20de%20emiss%C3%A3o%20de%20NF-e%20da%20SteinSoft.';

const PLANS = [
  { franquia: '1.000',  preco: 'R$\u00a0149,00',   perNota: '~R$\u00a00,15' },
  { franquia: '2.000',  preco: 'R$\u00a0260,00',   perNota: '~R$\u00a00,13' },
  { franquia: '5.000',  preco: 'R$\u00a0450,00',   perNota: '~R$\u00a00,09', popular: true },
  { franquia: '10.000', preco: 'R$\u00a0700,00',   perNota: '~R$\u00a00,07' },
  { franquia: '20.000', preco: 'R$\u00a01.000,00', perNota: '~R$\u00a00,05' },
  { franquia: '50.000', preco: 'R$\u00a01.800,00', perNota: '~R$\u00a00,04' },
];

const BADGES = [
  { label: 'SEFAZ Homologado', icon: <FaShieldAlt /> },
  { label: 'XML + DANFE',      icon: <FaFileAlt />   },
  { label: 'Resposta < 2s',    icon: <FaBolt />       },
  { label: '99.9% Uptime',     icon: <FaCheckCircle />},
  { label: 'Suporte Técnico',  icon: <FaHeadset />    },
];

const DOC_TYPES = [
  {
    sigla: 'NF-e',
    nome: 'Nota Fiscal Eletrônica',
    desc: 'Emissão de notas fiscais para circulação de mercadorias. Válida em todo território nacional com integração direta à SEFAZ.',
    color: 'green',
    icon: <FaFileInvoiceDollar />,
  },
  {
    sigla: 'NFS-e',
    nome: 'Nota Fiscal de Serviços',
    desc: 'Emissão para prestadores de serviço com suporte a centenas de municípios brasileiros e cálculo automático de ISS.',
    color: 'blue',
    icon: <FaFileCircleCheck />,
  },
  {
    sigla: 'NFC-e',
    nome: 'Nota Fiscal do Consumidor',
    desc: 'Solução para varejo com emissão rápida no ponto de venda, modo offline e integração com impressoras fiscais.',
    color: 'amber',
    icon: <FaReceipt />,
  },
];

/* ─── snippets de código ──────────────────────────────────────── */
const SNIPPETS = {
  typescript: {
    label: 'TypeScript / JS',
    lang: 'TS',
    lines: [
      { t: 'keyword', v: 'import' }, { t: 'plain', v: ' { ' }, { t: 'class', v: 'SteinNFe' }, { t: 'plain', v: ' } ' }, { t: 'keyword', v: 'from' }, { t: 'string', v: " '@steinsoft/nfe-sdk'" }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'keyword', v: 'const' }, { t: 'plain', v: ' client = ' }, { t: 'keyword', v: 'new' }, { t: 'plain', v: ' ' }, { t: 'class', v: 'SteinNFe' }, { t: 'plain', v: '({ ' }, { t: 'prop', v: 'apiKey' }, { t: 'plain', v: ': ' }, { t: 'string', v: "'sk-stein-xxxx'" }, { t: 'plain', v: ' });' },
      { t: 'break' },
      { t: 'keyword', v: 'const' }, { t: 'plain', v: ' nota = ' }, { t: 'keyword', v: 'await' }, { t: 'plain', v: ' client.' }, { t: 'prop', v: 'nfe' }, { t: 'plain', v: '.' }, { t: 'fn', v: 'emitir' }, { t: 'plain', v: '({' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'prop', v: 'cnpjEmitente' }, { t: 'plain', v: ': ' }, { t: 'string', v: "'00.000.000/0001-00'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'prop', v: 'natureza' }, { t: 'plain', v: ':    ' }, { t: 'string', v: "'Venda de mercadorias'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'prop', v: 'valorTotal' }, { t: 'plain', v: ':  ' }, { t: 'number', v: '1500.00' }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'prop', v: 'itens' }, { t: 'plain', v: ': [{' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'prop', v: 'descricao' }, { t: 'plain', v: ': ' }, { t: 'string', v: "'Produto Exemplo'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'prop', v: 'ncm' }, { t: 'plain', v: ':       ' }, { t: 'string', v: "'84713012'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'prop', v: 'cfop' }, { t: 'plain', v: ':      ' }, { t: 'string', v: "'5102'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'prop', v: 'valor' }, { t: 'plain', v: ':     ' }, { t: 'number', v: '1500.00' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'plain', v: '}],' },
      { t: 'break' },
      { t: 'plain', v: '});' },
      { t: 'break' },
      { t: 'break' },
      { t: 'comment', v: '// chave de acesso com 44 dígitos' },
      { t: 'break' },
      { t: 'fn', v: 'console' }, { t: 'plain', v: '.' }, { t: 'fn', v: 'log' }, { t: 'plain', v: '(nota.' }, { t: 'prop', v: 'chaveAcesso' }, { t: 'plain', v: ');' },
    ],
  },
  php: {
    label: 'PHP',
    lang: 'PHP',
    lines: [
      { t: 'plain', v: '<?php' },
      { t: 'break' },
      { t: 'keyword', v: 'require_once' }, { t: 'string', v: " 'vendor/autoload.php'" }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'keyword', v: 'use' }, { t: 'class', v: ' SteinSoft\\NFeSDK\\Client' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'break' },
      { t: 'variable', v: '$client' }, { t: 'plain', v: ' = ' }, { t: 'keyword', v: 'new' }, { t: 'plain', v: ' ' }, { t: 'class', v: 'Client' }, { t: 'plain', v: '(' }, { t: 'prop', v: 'apiKey' }, { t: 'plain', v: ': ' }, { t: 'string', v: "'sk-stein-xxxx'" }, { t: 'plain', v: ');' },
      { t: 'break' },
      { t: 'break' },
      { t: 'variable', v: '$nota' }, { t: 'plain', v: ' = ' }, { t: 'variable', v: '$client' }, { t: 'plain', v: '->' }, { t: 'prop', v: 'nfe' }, { t: 'plain', v: '->' }, { t: 'fn', v: 'emitir' }, { t: 'plain', v: '([' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'string', v: "'cnpj_emitente'" }, { t: 'plain', v: ' => ' }, { t: 'string', v: "'00.000.000/0001-00'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'string', v: "'natureza'" }, { t: 'plain', v: '      => ' }, { t: 'string', v: "'Venda de mercadorias'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'string', v: "'valor_total'" }, { t: 'plain', v: '   => ' }, { t: 'number', v: '1500.00' }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'string', v: "'itens'" }, { t: 'plain', v: '         => [[' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'string', v: "'descricao'" }, { t: 'plain', v: ' => ' }, { t: 'string', v: "'Produto Exemplo'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'string', v: "'ncm'" }, { t: 'plain', v: '      => ' }, { t: 'string', v: "'84713012'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'string', v: "'cfop'" }, { t: 'plain', v: '     => ' }, { t: 'string', v: "'5102'" }, { t: 'plain', v: ',' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'string', v: "'valor'" }, { t: 'plain', v: '    => ' }, { t: 'number', v: '1500.00' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'plain', v: ']],' },
      { t: 'break' },
      { t: 'plain', v: ']);' },
      { t: 'break' },
      { t: 'break' },
      { t: 'fn', v: 'echo' }, { t: 'plain', v: ' ' }, { t: 'variable', v: '$nota' }, { t: 'plain', v: '->' }, { t: 'prop', v: 'chave_acesso' }, { t: 'plain', v: ';' }, { t: 'comment', v: ' // 44 dígitos' },
    ],
  },
  delphi: {
    label: 'Delphi',
    lang: 'DEL',
    lines: [
      { t: 'keyword', v: 'uses' }, { t: 'class', v: ' SteinSoft.NFeSDK' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'break' },
      { t: 'keyword', v: 'var' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'variable', v: 'Client' }, { t: 'plain', v: ' : ' }, { t: 'class', v: 'TSteinNFeClient' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'variable', v: 'Config' }, { t: 'plain', v: ' : ' }, { t: 'class', v: 'TNFeConfig' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'variable', v: 'Nota' }, { t: 'plain', v: '   : ' }, { t: 'class', v: 'TNFeResponse' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'keyword', v: 'begin' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'variable', v: 'Client' }, { t: 'plain', v: ' := ' }, { t: 'class', v: 'TSteinNFeClient' }, { t: 'plain', v: '.' }, { t: 'fn', v: 'Create' }, { t: 'plain', v: '(' }, { t: 'string', v: "'sk-stein-xxxx'" }, { t: 'plain', v: ');' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'keyword', v: 'try' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'variable', v: 'Config' }, { t: 'plain', v: '.' }, { t: 'prop', v: 'CNPJEmitente' }, { t: 'plain', v: ' := ' }, { t: 'string', v: "'00.000.000/0001-00'" }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'variable', v: 'Config' }, { t: 'plain', v: '.' }, { t: 'prop', v: 'Natureza' }, { t: 'plain', v: '     := ' }, { t: 'string', v: "'Venda de mercadorias'" }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'variable', v: 'Config' }, { t: 'plain', v: '.' }, { t: 'prop', v: 'ValorTotal' }, { t: 'plain', v: '   := ' }, { t: 'number', v: '1500.00' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'variable', v: 'Nota' }, { t: 'plain', v: '   := ' }, { t: 'variable', v: 'Client' }, { t: 'plain', v: '.' }, { t: 'prop', v: 'NFe' }, { t: 'plain', v: '.' }, { t: 'fn', v: 'Emitir' }, { t: 'plain', v: '(' }, { t: 'variable', v: 'Config' }, { t: 'plain', v: ');' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'fn', v: 'ShowMessage' }, { t: 'plain', v: '(' }, { t: 'variable', v: 'Nota' }, { t: 'plain', v: '.' }, { t: 'prop', v: 'ChaveAcesso' }, { t: 'plain', v: ');' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'keyword', v: 'finally' },
      { t: 'break' },
      { t: 'indent4', v: '' }, { t: 'variable', v: 'Client' }, { t: 'plain', v: '.' }, { t: 'fn', v: 'Free' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'indent2', v: '' }, { t: 'keyword', v: 'end' }, { t: 'plain', v: ';' },
      { t: 'break' },
      { t: 'keyword', v: 'end' }, { t: 'plain', v: ';' },
    ],
  },
};

/* ─── helpers ─────────────────────────────────────────────────── */
const reveal = (delay = 0) => ({
  initial:    { opacity: 0, y: 32 },
  whileInView:{ opacity: 1, y: 0  },
  viewport:   { once: true        },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

function CodeLine({ tokens }) {
  return (
    <span className={styles.codeLine}>
      {tokens.map((tk, i) => {
        if (tk.t === 'break')   return <br key={i} />;
        if (tk.t === 'indent2') return <span key={i} className={styles.indent2} />;
        if (tk.t === 'indent4') return <span key={i} className={styles.indent4} />;
        return <span key={i} className={styles[`tok_${tk.t}`]}>{tk.v}</span>;
      })}
    </span>
  );
}

/* ─── componente principal ────────────────────────────────────── */
export default function NotaFiscal() {
  const [activeTab, setActiveTab] = useState('typescript');
  const pricingRef = useRef(null);
  const pricingInView = useInView(pricingRef, { once: true });

  const snippet = SNIPPETS[activeTab];

  return (
    <div className={styles.page}>
      <PageSEO
        title="API de NF-e, NFS-e e NFC-e"
        description="Emita NF-e, NFS-e e NFC-e com a API fiscal da SteinSoft. SDKs em PHP, Delphi e TypeScript. Pacotes de créditos a partir de R$ 149,00 por 1.000 documentos, sem mensalidade."
        path="/NotaFiscal"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'SteinNFe — API de Emissão Fiscal',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: 'API REST para emissão de NF-e, NFS-e e NFC-e com SDKs em PHP, Delphi e TypeScript.',
          url: 'https://steinsoft.com.br/NotaFiscal',
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'BRL',
            lowPrice: '149',
            highPrice: '1800',
          },
        }}
      />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} aria-hidden />

        <div className={styles.heroInner}>
          <motion.div className={styles.heroLeft} {...reveal(0)}>
            <div className={styles.eyebrow}>
              <span className={styles.eyebrowDot} />
              API Fiscal · SEFAZ Homologado
            </div>

            <h1 className={styles.heroTitle}>
              <span className={styles.titleMono}>SteinNFe</span>
              <span className={styles.titleSub}>
                Emita <em>NF-e</em>, <em>NFS-e</em> e <em>NFC-e</em>
                <br />com uma única API
              </span>
            </h1>

            <p className={styles.heroDesc}>
              Integre emissão fiscal à sua aplicação em minutos.
              SDKs prontos para PHP, Delphi e TypeScript — sem burocracia, com uptime de 99.9%.
            </p>

            <div className={styles.heroActions}>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btnPrimary}
              >
                <FaWhatsapp /> Falar com um consultor
              </a>
              <a href="#planos" className={styles.btnGhost}>
                Ver pacotes e preços
              </a>
            </div>

            <div className={styles.badgeStrip}>
              {BADGES.map((b) => (
                <span key={b.label} className={styles.badge}>
                  {b.icon} {b.label}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div className={styles.heroRight} {...reveal(0.15)}>
            <div className={styles.terminalWindow}>
              <div className={styles.terminalBar}>
                <span className={styles.dot} data-color="red"   />
                <span className={styles.dot} data-color="yellow"/>
                <span className={styles.dot} data-color="green" />
                <span className={styles.terminalTitle}>steinNFe.ts</span>
              </div>
              <pre className={styles.terminalCode}>
                <CodeLine tokens={SNIPPETS.typescript.lines} />
              </pre>
            </div>

            <div className={styles.floatStat} data-pos="tl">
              <FaClock className={styles.floatIcon} />
              <div>
                <strong>&lt; 2s</strong>
                <span>tempo de resposta</span>
              </div>
            </div>
            <div className={styles.floatStat} data-pos="br">
              <FaShieldAlt className={styles.floatIcon} />
              <div>
                <strong>99.9%</strong>
                <span>disponibilidade</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TIPOS DE DOCUMENTO ──────────────────────────────────── */}
      <section className={styles.docSection}>
        <div className={styles.sectionInner}>
          <motion.div className={styles.sectionHeader} {...reveal()}>
            <h2 className={styles.sectionTitle}>
              Três documentos, <span className={styles.accent}>uma API</span>
            </h2>
            <p className={styles.sectionSub}>
              Cubra todos os cenários fiscais do seu negócio sem precisar integrar múltiplos fornecedores.
            </p>
          </motion.div>

          <div className={styles.docGrid}>
            {DOC_TYPES.map((d, i) => (
              <motion.div
                key={d.sigla}
                className={styles.docCard}
                data-color={d.color}
                {...reveal(i * 0.1)}
              >
                <div className={styles.docIconWrap}>{d.icon}</div>
                <div className={styles.docSigla}>{d.sigla}</div>
                <div className={styles.docNome}>{d.nome}</div>
                <p className={styles.docDesc}>{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SDKs ──────────────────────────────────────────────── */}
      <section className={styles.sdkSection}>
        <div className={styles.sectionInner}>
          <motion.div className={styles.sectionHeader} {...reveal()}>
            <h2 className={styles.sectionTitle}>
              Integre na sua <span className={styles.accent}>linguagem favorita</span>
            </h2>
            <p className={styles.sectionSub}>
              SDKs oficiais disponíveis. Instale, configure sua chave e emita a primeira nota em minutos.
            </p>
          </motion.div>

          <motion.div className={styles.codeWrapper} {...reveal(0.1)}>
            <div className={styles.tabBar}>
              {Object.entries(SNIPPETS).map(([key, s]) => (
                <button
                  key={key}
                  className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  <span className={styles.tabBadge} data-lang={s.lang}>{s.lang}</span>
                  {s.label}
                </button>
              ))}
            </div>

            <div className={styles.codeWindow}>
              <div className={styles.codeWindowBar}>
                <span className={styles.dot} data-color="red"   />
                <span className={styles.dot} data-color="yellow"/>
                <span className={styles.dot} data-color="green" />
                <span className={styles.codeWindowTitle}>
                  {snippet.label.toLowerCase().replace('/', '-')}-example.{activeTab === 'typescript' ? 'ts' : activeTab === 'php' ? 'php' : 'pas'}
                </span>
              </div>
              <div className={styles.codeBody}>
                <div className={styles.lineNumbers}>
                  {Array.from({ length: 22 }, (_, n) => (
                    <span key={n}>{n + 1}</span>
                  ))}
                </div>
                <pre className={styles.codeContent} key={activeTab}>
                  <motion.code
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.25 }}
                  >
                    <CodeLine tokens={snippet.lines} />
                  </motion.code>
                </pre>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PLANOS ────────────────────────────────────────────── */}
      <section className={styles.pricingSection} id="planos" ref={pricingRef}>
        <div className={styles.sectionInner}>
          <motion.div className={styles.sectionHeader} {...reveal()}>
            <h2 className={styles.sectionTitle}>
              Créditos com <span className={styles.accent}>preço agressivo</span>
            </h2>
            <p className={styles.sectionSub}>
              Sem mensalidade: você compra um pacote de créditos por um valor fixo e consome conforme emite. Sem setup, sem fidelidade.
            </p>
          </motion.div>

          <motion.div className={styles.pricingTable} {...reveal(0.1)}>
            <div className={styles.pricingHeader}>
              <span>Créditos</span>
              <span>Valor do pacote</span>
              <span>Custo por nota</span>
              <span></span>
            </div>

            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.franquia}
                className={`${styles.pricingRow} ${plan.popular ? styles.pricingRowPopular : ''}`}
                initial={{ opacity: 0, x: -20 }}
                animate={pricingInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {plan.popular && (
                  <span className={styles.popularBadge}>Mais escolhido</span>
                )}
                <span className={styles.pricingFranquia}>
                  <span className={styles.franquiaNum}>{plan.franquia}</span>
                  <span className={styles.franquiaLabel}>créditos</span>
                </span>
                <span className={styles.pricingPrice}>{plan.preco}</span>
                <span className={styles.pricingPer}>{plan.perNota}</span>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.pricingCta}
                >
                  Contratar
                </a>
              </motion.div>
            ))}
          </motion.div>

          <motion.p className={styles.pricingNote} {...reveal(0.3)}>
            1 crédito = 1 nota autorizada em produção (NF-e, NFS-e ou NFC-e). Rejeições e emissões em homologação não consomem créditos. Acabou o saldo? É só comprar um novo pacote.<br />Todos os pacotes incluem: certificado A1, XML + DANFE, webhooks em tempo real e suporte técnico via chat.
          </motion.p>
        </div>
      </section>

      {/* ── CTA FINAL ────────────────────────────────────────── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaGlow} aria-hidden />
        <motion.div className={styles.ctaInner} {...reveal()}>
          <div className={styles.ctaIcon}>
            <FaFileInvoiceDollar />
          </div>
          <h2 className={styles.ctaTitle}>
            Pronto para emitir a<br />
            <span className={styles.accent}>primeira nota fiscal?</span>
          </h2>
          <p className={styles.ctaDesc}>
            Fale agora com um consultor SteinSoft e receba orientação personalizada sobre o melhor pacote de créditos para o seu volume.
          </p>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaBtn}
          >
            <FaWhatsapp className={styles.ctaBtnIcon} />
            Falar no WhatsApp
          </a>
        </motion.div>
      </section>
    </div>
  );
}
